<template>
  <v-container fluid grid-list-md>
    <AppTitleBar>
      <template slot="title">{{ $t('title') }}</template>
      <template slot="actions">
        <v-tooltip top transition="fade-transition">
          <template v-slot:activator="{ on }">
            <v-btn icon small @click="create" v-on="on" v-if="$flow.isAllowed('create')">
              <v-icon small v-text="$vuetify.icons.values.add" />
            </v-btn>
          </template>
          <span>{{ $t('new') }}</span>
        </v-tooltip>
        <v-menu bottom left min-width="150" v-if="$flow.isAllowed('manage')">
          <template #activator="{ on: menu }">
            <v-tooltip top transition="fade-transition">
              <template #activator="{ on: tooltip }">
                <v-btn icon small slot="activator">
                  <v-icon small v-text="$vuetify.icons.values.options" v-on="{ ...tooltip, ...menu }" />
                </v-btn>
              </template>
              <span>{{ $t('labels.actions') }}</span>
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item v-if="$flow.isAllowed('manage')" @click="showRestore">
              <v-list-item-title>{{ $t('restore') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </AppTitleBar>
    <v-row no-gutters justify="end">
      <v-col cols="12" sm="6" md="4" lg="3">
        <SingleSearchFilter :context="context" :fields="searchFields" />
      </v-col>
    </v-row>
    <v-loading v-if="loading" />
    <v-data-iterator
      v-else
      :items="flows"
      row
      wrap
      :server-items-length="flowsCount"
      :page.sync="page"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :items-per-page.sync="itemsPerPage"
      :footer-props="{ itemsPerPageOptions: itemsPerPageArray }"
    >
      <template v-slot:default="{ items }">
        <v-row>
          <v-col v-for="item in items" :key="item.id" cols="12" sm="6" md="4" lg="3">
            <VWidget enable-footer-actions css-class="hover-border">
              <template slot="title">{{ item.display }}</template>
              <template slot="content">
                <p>{{ item.description }}</p>
              </template>
              <template slot="footer-actions">
                <v-btn small icon @click="goToFlow(item.id)">
                  <v-icon small v-text="$vuetify.icons.values.view" />
                </v-btn>
              </template>
            </VWidget>
          </v-col>
        </v-row>
      </template>
    </v-data-iterator>
    <router-view></router-view>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { AppTitleBar, VWidget, SingleSearchFilter, VLoading } from '@argon/app/components'
import { ConfirmDeleteFlowDialog } from '../components'
import { Datatable } from '@argon/app/mixins'
import { NAMESPACE } from '../store'
import { NAMESPACE as REALTIME_NAMESPACE } from '@argon/realtime/store'
import flowPerimeter from '../perimeters.js'
import { create } from 'vue-modal-dialogs'

const confirmDeleteAction = create(ConfirmDeleteFlowDialog)

export default {
  perimeters: [flowPerimeter],
  mixins: [Datatable],
  components: { AppTitleBar, VWidget, SingleSearchFilter, VLoading },
  props: {
    orgRef: String
  },
  data: () => ({
    searchFields: [{ label: 'display', type: 'text' }],
    loading: true,
    context: NAMESPACE
  }),
  computed: {
    ...mapState(NAMESPACE, ['flows', 'templates', 'flowsCount']),
    realtimeKey() {
      return this.$store.getters[`${REALTIME_NAMESPACE}/realtimeKey`]({
        category: 'flowsList',
        identifier: 'allFlows'
      })
    }
  },
  methods: {
    ...mapActions(REALTIME_NAMESPACE, ['addListener', 'removeListener']),
    ...mapActions(NAMESPACE, [
      'listFlows',
      'clearFlows',
      'listTemplates',
      'clearTemplates',
      'createFlow',
      'deleteFlow'
    ]),
    goToFlow(id) {
      this.$router.push({
        name: 'flow',
        params: { ...this.$router.params, flowId: id }
      })
    },
    showRestore() {
      this.$router.push({ name: 'flowRestore' })
    },
    create() {
      this.$router.push({ name: 'newFlow' })
    },
    destroy(flow) {
      return confirmDeleteAction().then((res) => {
        if (res) {
          this.deleteFlow(flow.id).then(() => {
            this.fetchData()
          })
        }
      })
    },
    fetchData() {
      this.loading = true
      return this.listFlows({ pager: this.pager }).finally(() => {
        this.loading = false
      })
    }
  },
  watch: {
    realtimeKey: function (v, oldV) {
      if (oldV) {
        this.fetchData()
      }
    }
  },
  created() {
    this.fetchData().then(() => {
      this.addListener({
        category: 'flowsList',
        identifier: 'allFlows'
      })
    })
  },
  destroyed() {
    this.removeListener({ category: 'flowsList', identifier: 'allFlows' })
    this.clearFlows()
    this.clearTemplates()
  }
}
</script>

<i18n>
{
  "en": {
    "new": "New Flow",
    "restore": "@:labels.restore",
    "title": "@:labels.flows"
  },
  "fr": {
    "new": "Nouveau flux",
    "restore": "@:labels.restore",
    "title": "@:labels.flows"
  }
}
</i18n>
